import { useState } from "react";

// Compact, READ-ONLY pipeline progression: a slim dot rail with a filled progress track. The current
// stage name + step number are always shown below the rail; hovering/touching a dot reveals its name.
// Stage is changed only from the Status dropdown on the job detail page — this strip never mutates it.
export const JobStageTracker = ({ stages = [], current }) => {
  const idx = stages.indexOf(current);
  const total = stages.length;
  const [hover, setHover] = useState(null); // hovered/touched { name, i }
  const pct = idx > -1 && total > 1 ? (idx / (total - 1)) * 100 : 0;
  const dotLeft = (i) => (total > 1 ? (i / (total - 1)) * 100 : 0);

  return (
    <div className="overflow-x-clip rounded-xl border border-brand-line bg-white px-4 py-3" data-testid="job-stage-tracker">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Pipeline stage</p>
        <span className="text-[11px] text-slate-400">Change stage from the Status dropdown above</span>
      </div>

      <div className="relative mt-5 px-1">
        {hover && (
          <div
            className="pointer-events-none absolute -top-2 z-10 -translate-y-full -translate-x-1/2 whitespace-nowrap rounded-md bg-brand-ink px-2 py-1 text-[11px] font-medium text-white shadow-md"
            style={{ left: `clamp(28px, calc(1px + ${dotLeft(hover.i)}% * (100% - 8px) / 100%), calc(100% - 28px))` }}
            data-testid="stage-tooltip"
          >
            {hover.i + 1}. {hover.name}
          </div>
        )}
        <div className="absolute inset-x-1 top-1/2 h-0.5 -translate-y-1/2 rounded bg-slate-100" />
        <div
          className="absolute left-1 top-1/2 h-0.5 -translate-y-1/2 rounded bg-emerald-400 transition-all duration-300"
          style={{ width: `calc(${pct}% * (100% - 8px) / 100%)` }}
        />
        <div className="relative flex items-center justify-between">
          {stages.map((s, i) => {
            const done = idx > -1 && i < idx;
            const isCurrent = i === idx;
            const dot = isCurrent
              ? "h-4 w-4 bg-brand-ink ring-2 ring-brand-ink/25"
              : done
              ? "h-3 w-3 bg-emerald-500"
              : "h-3 w-3 bg-slate-300";
            return (
              <button
                key={s}
                type="button"
                title={s}
                onMouseEnter={() => setHover({ name: s, i })}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover({ name: s, i })}
                onBlur={() => setHover(null)}
                onTouchStart={() => setHover({ name: s, i })}
                data-testid={`stage-${i}${isCurrent ? "-current" : ""}`}
                className={`flex cursor-default items-center justify-center rounded-full ${dot}`}
              />
            );
          })}
        </div>
      </div>

      <p className="mt-3 text-sm font-semibold text-brand-ink" data-testid="stage-current-label">
        {current || "—"}
        {idx > -1 ? ` — Step ${idx + 1} of ${total}` : ""}
      </p>
    </div>
  );
};
